"use client"

import { colors, mediaQueries, sizes } from "@/ui/theme/theme"
import { replaceRichTextLinks } from "@/utils/replaceRichTextLinks"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import BulletList from "./BulletList"
import CommonTitle from "./CommonTitle"

interface ContentItem {
  type: string
  content: string | string[]
  href?: string
  level?: number
  external?: boolean
  [key: string]: any // Allow additional properties
}

interface GeneralTextContentProps {
  data: any
  className?: string
}

const StyledCommonTitle = styled(CommonTitle)`
  align-items: flex-start;
  width: 100%;
  margin-bottom: 0;
`
const StyledSmallerCommonTitle = styled(CommonTitle)`
  align-items: flex-start;
  width: 100%;
  margin-bottom: 0;
  h2 {
    font-weight: 700;
    color: ${colors.black};
  }
  h4 {
    font-weight: 400;
    color: ${colors.red};
    font-size: 16px;
    margin-bottom: 4px;
    ${mediaQueries.desktopM} {
      margin-bottom: 8px;
    }
  }
`

const StyledText = styled(Typography)`
  color: ${colors.black};
  a {
    display: flex;
    flex-direction: row;
    color: ${colors.black};
    &::before {
      width: 22px;
      height: 22px;
      content: url("/icons/info.svg");
      margin-right: ${sizes.baseSingle};
    }
  }
`

const BulletNumberList = styled(Typography)`
  display: flex;
  flex-direction: column;
  ul {
    display: flex;
    flex-direction: column;
    padding: 0;
    gap: 8px;
    list-style-type: none; // Remove default list styling
    counter-reset: list-counter; // Initialize counter
    li {
      position: relative;
      color: ${colors.black};
      gap: 10px;
      padding-left: 24px; // Add padding to the left for the counter
      counter-increment: list-counter; // Increment counter
      &::before {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        background: ${colors.red};
        color: ${colors.white};
        border-radius: 50px;
        left: 0;
        content: counter(list-counter); // Use counter value
        font-family: "Brown Trial Heb";
        font-size: 18px;
        font-style: normal;
        font-weight: 700;
        line-height: 120%;
      }
    }
  }
`

const StyledHorizontalList = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  ${mediaQueries.mobile} {
    flex-direction: column;
    align-items: center;
  }
  > * {
    display: flex;
    flex-direction: row;
    width: fit-content;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 0 ${sizes.baseSingle};
    ${mediaQueries.mobile} {
      flex-direction: column;
    }
    p {
      color: ${colors.black};
    }
    a {
      color: ${colors.black};
    }
    &::after {
      display: flex;
      width: 2px;
      height: 18px;
      content: "";
      background-color: ${colors.red};
      ${mediaQueries.mobile} {
        width: 18px;
        height: 2px;
      }
    }
    &:last-child {
      &::after {
        display: none;
      }
    }
  }
`

const StyledContent = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  ${mediaQueries.tabletDown} {
    padding-top: 60px;
  }
`

const GeneralTextContent = ({ data, className }: GeneralTextContentProps) => {
  const contentItems = Array.isArray(data) ? data : Object.values(data)
  const keys = Object.keys(data)

  const renderItem = (item: ContentItem, index: number) => {
    const currKey = `content-item-${index}-${keys[index]}`
    // For items with HTML content, only render with dangerouslySetInnerHTML on client
    const hasHtmlContent = [
      "bullets",
      "bulletsV",
      "bulletsNumbers",
      "text",
    ].some((type) => keys[index].includes(type) || keys[index] === type)

    switch (keys[index]) {
      case "title":
      case (keys[index].match(/^title\d+$/) || {}).input:
        return (
          <StyledCommonTitle
            className="title"
            key={currKey}
            title={item?.value ? item?.value : item}
          />
        )

      case "subtitle":
      case (keys[index].match(/^redTitle\d+$/) || {}).input:
        return (
          <StyledSmallerCommonTitle
            className="subtitle"
            key={currKey}
            text={item?.value ? item?.value : item}
          />
        )

      case "redTitle":
      case (keys[index].match(/^subtitle\d+$/) || {}).input:
        return (
          <StyledSmallerCommonTitle
            className="red-title"
            key={currKey}
            subtitle={item?.value ? item?.value : item}
          />
        )

      case "bullets":
      case "bulletsV":
      case (keys[index].match(/^bullets\d+$/) || {}).input:
        return item.richText ? (
          <BulletList
            key={currKey}
            className="bullets"
            dangerouslySetInnerHTML={{
              __html: replaceRichTextLinks(item.richText as string),
            }}
          />
        ) : null

      case "bulletsNumbers":
      case (keys[index].match(/^bulletsNumbers\d+$/) || {}).input:
        return item.richText ? (
          <BulletNumberList
            key={currKey}
            className="bullets-numbers"
            dangerouslySetInnerHTML={{
              __html: replaceRichTextLinks(item.richText as string),
            }}
          />
        ) : null
      case "horizontalList":
        return (
          <StyledHorizontalList className="horizontal-list" key={currKey}>
            {item.map((element: any) => {
              const currInnerKey = `${currKey}-horizontal-list-item-${element.value}`
              // Handle different element types with switch case
              switch (element.type) {
                case "mail":
                  return (
                    <Box key={currInnerKey}>
                      <Typography key={currInnerKey} variant="body1">
                        {element.text}
                      </Typography>
                      <Typography variant="body1">
                        <a href={`mailto:${element.value}`}>{element.value}</a>
                      </Typography>
                    </Box>
                  )
                default:
                  return (
                    <Box key={currInnerKey}>
                      <Typography variant="body1">{element.text}</Typography>
                      <Typography
                        variant="body1"
                        dangerouslySetInnerHTML={{
                          __html: replaceRichTextLinks(
                            element.richText || element.value,
                          ),
                        }}
                      />
                    </Box>
                  )
              }
            })}
          </StyledHorizontalList>
        )
      case "text":
      default:
        return item?.richText || item?.value ? (
          <StyledText
            key={currKey}
            className="text"
            variant="body1"
            dangerouslySetInnerHTML={{
              __html: replaceRichTextLinks(
                item?.richText || (item?.value as string),
              ),
            }}
          />
        ) : null
    }
  }

  return (
    <StyledContent className={className}>
      {contentItems.map((item, index) => renderItem(item, index))}
    </StyledContent>
  )
}

export default GeneralTextContent
